import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import Workspace from './components/prototype/Workspace';
import NewAgendaItem from './components/prototype/NewAgendaItem';
import LoginHistory from './components/prototype/LoginHistory';
import logo from './assets/logo.png';
import CustomerInfoPage from './components/customer/CustomerInfoPage';

const API_BASE_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && typeof envUrl === 'string') {
    return envUrl.replace(/\/$/, '');
  }

  // Check if running in Capacitor (native app)
  if (typeof window !== 'undefined' && window.Capacitor) {
    // In native app, use production API URL or configure via environment
    // For now, use the same origin as the web app would use
    // You can set REACT_APP_API_URL in your build process
    return 'https://werkagenda.nl/api'; // Update this to your production API URL
  }

  if (typeof window !== 'undefined' && window.location?.port !== '3000') {
    return `${window.location.origin.replace(/\/$/, '')}/api`;
  }

  return 'http://localhost:5000/api';
})();
// Zet op false zodra de agendakoppeling weer beschikbaar is.
const MAINTENANCE_MODE = false;
const ENABLE_AGENDA_SYNC = process.env.REACT_APP_ENABLE_AGENDA_SYNC === 'true';
const STORAGE_KEY = 'werkagenda-auth';
const TOKEN_STORAGE_KEY = 'werkagenda-token';
const THEME_STORAGE_KEY = 'werkagenda-theme';

// Configure axios to include token in all requests
axios.interceptors.request.use(
  (config) => {
    const allowedSync = ENABLE_AGENDA_SYNC && config.method?.toLowerCase() === 'post' && config.url === `${API_BASE_URL}/ververs-data`;
    if ((config.method || 'get').toLowerCase() !== 'get' && !config.url.endsWith('/login') && !allowedSync) {
      return Promise.reject(new Error('Deze lokale versie wijzigt geen live gegevens.'));
    }
    if (typeof window !== 'undefined') {
      const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (unauthorized) - token expired or invalid
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth and token, redirect to login
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        // Optionally reload to show login screen
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

const resolveInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === 'dark' || storedTheme === 'light') {
    return storedTheme;
  }

  // Default is always light mode, regardless of system preference
  return 'light';
};

const padToTwo = (value) => String(value).padStart(2, '0');

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    // Match format: "2025-11-13 08:30:00" or "2025-11-13T08:30:00"
    const isoMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i
    );
    if (isoMatch) {
      const [, year, month, day, hour, minute, second = '0'] = isoMatch;
      // Create date in local timezone (not UTC)
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }

    // Try parsing as local date string (MySQL DATETIME format)
    // Format: "YYYY-MM-DD HH:MM:SS"
    const mysqlMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (mysqlMatch) {
      const [, year, month, day, hour, minute, second] = mysqlMatch;
      // Create date in local timezone
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getLocalDateKey = (value) => {
  const date = parseDateValue(value);
  if (!date) return null;
  return [
    date.getFullYear(),
    padToTwo(date.getMonth() + 1),
    padToTwo(date.getDate())
  ].join('-');
};

// Parse ISO date string and return Date object, using UTC methods for extraction
// This ensures we get the UTC values directly from the string
const parseUTCDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  
  // Parse as Date object - this handles UTC strings correctly
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  
  return date;
};

// Get start datetime: use actie_tijd_start directly (now contains full date)
const getItemStartDate = (item) => {
  const actieTijdStart = item.actie_tijd_start ?? item.ActieTijdStart ?? item.actie_tijd ?? item.ActieTijd;
  if (actieTijdStart) {
    return parseUTCDate(actieTijdStart);
  }
  return null;
};

// Get stop datetime: use actie_tijd_stop directly (now contains full date)
const getItemStopDate = (item) => {
  const actieTijdStop = item.actie_tijd_stop ?? item.ActieTijdStop;
  if (actieTijdStop) {
    return parseUTCDate(actieTijdStop);
  }
  return null;
};

const getItemDateKey = (item) => {
  // Use datum field directly for date key
  const datum = item.datum ?? item.Datum;
  if (datum) {
    const date = parseUTCDate(datum);
    if (date) {
      return `${date.getUTCFullYear()}-${padToTwo(date.getUTCMonth() + 1)}-${padToTwo(date.getUTCDate())}`;
    }
  }
  // Fallback: use actie_tijd_start
  const startDate = getItemStartDate(item);
  if (startDate) {
    return `${startDate.getUTCFullYear()}-${padToTwo(startDate.getUTCMonth() + 1)}-${padToTwo(startDate.getUTCDate())}`;
  }
  return null;
};

const extractMedewerkerCodes = (item) => {
  if (Array.isArray(item.medewerkers)) {
    return item.medewerkers.map(m => m.code + (m.onzeker ? '?' : ''));
  }
  return [...new Set(String(item.medewerker_veld || '').split(/[\s/]+/).filter(Boolean).map(c => c.toUpperCase()))];
};
const isCurrentMedewerker = (codes, activeCode) => Boolean(activeCode) &&
  codes.some(code => ['ALLE', activeCode].includes(code.replace(/\?$/, '')));


function App() {
  const [auth, setAuth] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored || !token) {
        // Clear both if either is missing
        if (stored) window.localStorage.removeItem(STORAGE_KEY);
        if (token) window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
      }
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.code === 'string') {
        return parsed;
      }
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading auth from storage:', error);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
      return null;
    }
  });
  const [loginForm, setLoginForm] = useState({ code: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [filters, setFilters] = useState({
    medewerker: '',
    weekOffset: 0
  });
  const [vandaagOffset, setVandaagOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');
  const [theme, setTheme] = useState(resolveInitialTheme);
  
  const [dashboardData, setDashboardData] = useState({
    vandaag: [],
    morgen: [],
    vandaagVGK: [],
    weekOL: [],
    klantenWeek: [],
    vandaagDMG2: [],
    afwezigWeek: [],
    eigenVolgendeWeek: []
  });
  const [medewerkers, setMedewerkers] = useState([]);
  const [loading, setLoading] = useState({ all: false, vandaag: false });
  const [showMedewerkerPanel, setShowMedewerkerPanel] = useState(false);
  const scrollPositionRef = useRef(null);
  const [viewMode, setViewMode] = useState('dashboards');
  const [employeeDirectory, setEmployeeDirectory] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [dayAgendaItems, setDayAgendaItems] = useState([]);
  const [dayAgendaLoading, setDayAgendaLoading] = useState(false);
  const [dayAgendaError, setDayAgendaError] = useState('');
  const dayRequestRef = useRef(0);
  const [dayAgendaOffset, setDayAgendaOffset] = useState(0);
  const [initializing, setInitializing] = useState(false);
  const [selectedAgendaItem, setSelectedAgendaItem] = useState(null);
  const isAuthenticated = Boolean(auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (MAINTENANCE_MODE || !isAuthenticated) return;
    fetchMedewerkers();
  }, [isAuthenticated, filters.weekOffset]);

  useEffect(() => {
    if (MAINTENANCE_MODE || !isAuthenticated || viewMode !== 'dashboards') return;
    fetchAllDashboards();
  }, [isAuthenticated, filters.medewerker, filters.weekOffset, viewMode, auth?.code]);

  useEffect(() => {
    if (MAINTENANCE_MODE || !isAuthenticated || viewMode !== 'dashboards') return;
    fetchVandaagDashboard();
  }, [isAuthenticated, filters.medewerker, vandaagOffset, viewMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (!body) return;

    const shouldLockBodyScroll = selectedAgendaItem !== null && viewMode === 'dashboards';

    if (shouldLockBodyScroll) {
      body.classList.add('body-scroll-lock-active');
    } else {
      body.classList.remove('body-scroll-lock-active');
    }

    return () => {
      body.classList.remove('body-scroll-lock-active');
    };
  }, [selectedAgendaItem, viewMode]);

  useEffect(() => {
    if (MAINTENANCE_MODE || !isAuthenticated || viewMode !== 'agenda') return;
    fetchAgendaItems();
  }, [isAuthenticated, filters.medewerker, filters.weekOffset, viewMode]);

  useEffect(() => {
    if (MAINTENANCE_MODE || !isAuthenticated || viewMode !== 'dagagenda') return;
    fetchDayAgendaItems();
  }, [isAuthenticated, filters.medewerker, dayAgendaOffset, viewMode]);

  useEffect(() => {
    if (viewMode === 'beheer' && !auth?.isAdmin) {
      setViewMode('dashboards');
    }
  }, [auth?.isAdmin, viewMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowMedewerkerPanel(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const body = document.body;
    const themeClass = theme === 'dark' ? 'theme-dark' : 'theme-light';

    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(themeClass);

    if (body) {
      body.classList.remove('theme-dark', 'theme-light');
      body.classList.add(themeClass);
    }

    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  const fetchEmployeeDirectory = useCallback(async () => {
    setDirectoryLoading(true);
    setDirectoryError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/agenda-items`);
      const all = new Map([['ALLE', { code: 'ALLE', volledige_naam: 'Iedereen' }]]);
      for (const item of response.data.data || []) {
        for (const employee of item.medewerkers || []) all.set(employee.code, employee);
      }
      setEmployeeDirectory([...all.values()].sort((a, b) => a.code === 'ALLE' ? -1 : b.code === 'ALLE' ? 1 : (a.volledige_naam || a.code).localeCompare(b.volledige_naam || b.code)));
    } catch {
      setDirectoryError('Medewerkers konden niet worden geladen.');
    } finally { setDirectoryLoading(false); }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchEmployeeDirectory();
    else setEmployeeDirectory([]);
  }, [isAuthenticated, fetchEmployeeDirectory]);

  const fetchMedewerkers = async () => {
    try {
      const weekParams = new URLSearchParams();
      weekParams.append('weekOffset', filters.weekOffset);
      const weekQuery = weekParams.toString();
      const weekUrl = `?${weekQuery}`;
      
      const response = await axios.get(`${API_BASE_URL}/dashboard/medewerkers-week${weekUrl}`);
      if (response.data.success) {
        setMedewerkers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching medewerkers:', error);
    }
  };

  const fetchVandaagDashboard = async () => {
    const params = new URLSearchParams();
    params.append('dayOffset', vandaagOffset);
    if (filters.medewerker) params.append('medewerker', filters.medewerker);
    const query = `?${params.toString()}`;

    try {
      setLoading((prev) => ({ ...prev, vandaag: true }));
      const response = await axios.get(`${API_BASE_URL}/dashboard/vandaag${query}`);
      setDashboardData((prev) => ({
        ...prev,
        vandaag: response.data.success ? response.data.data : []
      }));
    } catch (error) {
      console.error('Error fetching dashboard vandaag data:', error);
      setDashboardData((prev) => ({ ...prev, vandaag: [] }));
    } finally {
      setLoading((prev) => ({ ...prev, vandaag: false }));
    }
  };

  const fetchAllDashboards = async () => {
    // Only medewerker filter for morgen (no day offset here yet)
    const morgenParams = new URLSearchParams();
    if (filters.medewerker) morgenParams.append('medewerker', filters.medewerker);
    const morgenQuery = morgenParams.toString();
    const morgenUrl = morgenQuery ? `?${morgenQuery}` : '';

    // Week offset for week-based dashboards (always include, even if 0)
    const weekParams = new URLSearchParams();
    weekParams.append('weekOffset', filters.weekOffset);
    const weekQuery = weekParams.toString();
    const weekUrl = `?${weekQuery}`;
    const weekUrlAppend = `&${weekQuery}`; // For appending to existing query strings

    try {
      setLoading((prev) => ({ ...prev, all: true }));
      
      // Get eigen volgende week only if user is authenticated
      const eigenVolgendeWeekUrl = auth?.code ? `?medewerker=${encodeURIComponent(auth.code)}` : '';
      const eigenVolgendeWeekPromise = auth?.code 
        ? axios.get(`${API_BASE_URL}/dashboard/eigen-volgende-week${eigenVolgendeWeekUrl}`)
        : Promise.resolve({ data: { success: true, data: [] } });

      const [morgen, vandaagVGK, weekOL, klantenWeek, vandaagDMG2, afwezigWeek, eigenVolgendeWeek] = await Promise.all([
        axios.get(`${API_BASE_URL}/dashboard/morgen${morgenUrl}`),
        axios.get(`${API_BASE_URL}/dashboard/locatie-vandaag?locatie=VGK`),
        axios.get(`${API_BASE_URL}/dashboard/locatie-week?locatie=OL${weekUrlAppend}`),
        axios.get(`${API_BASE_URL}/dashboard/klanten-week${weekUrl}`),
        axios.get(`${API_BASE_URL}/dashboard/locatie-vandaag?locatie=DMG`),
        axios.get(`${API_BASE_URL}/dashboard/locatie-week?locatie=AFW${weekUrlAppend}`),
        eigenVolgendeWeekPromise
      ]);

      setDashboardData((prev) => ({
        ...prev,
        morgen: morgen.data.success ? morgen.data.data : [],
        vandaagVGK: vandaagVGK.data.success ? vandaagVGK.data.data : [],
        weekOL: weekOL.data.success ? weekOL.data.data : [],
        klantenWeek: klantenWeek.data.success ? klantenWeek.data.data : [],
        vandaagDMG2: vandaagDMG2.data.success ? vandaagDMG2.data.data : [],
        afwezigWeek: afwezigWeek.data.success ? afwezigWeek.data.data : [],
        eigenVolgendeWeek: eigenVolgendeWeek.data.success ? eigenVolgendeWeek.data.data : []
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading((prev) => ({ ...prev, all: false }));
      if (scrollPositionRef.current !== null) {
        window.requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollPositionRef.current ?? 0,
            left: 0,
            behavior: 'auto'
          });
          scrollPositionRef.current = null;
        });
      }
    }
  };

  const fetchAgendaItems = async () => {
    const params = new URLSearchParams();
    if (filters.medewerker) params.append('medewerker', filters.medewerker);
    params.append('weekOffset', filters.weekOffset);

    const query = params.toString();

    try {
      setAgendaLoading(true);
      const response = await axios.get(`${API_BASE_URL}/agenda-items${query ? `?${query}` : ''}`);
      if (response.data?.success) {
        setAgendaItems(response.data.data ?? []);
      } else {
        setAgendaItems([]);
      }
    } catch (error) {
      console.error('Error fetching agenda items:', error);
      setAgendaItems([]);
    } finally {
      setAgendaLoading(false);
    }
  };

  const fetchDayAgendaItems = async () => {
    const requestId = ++dayRequestRef.current;
    try {
      setDayAgendaError('');
      setDayAgendaLoading(true);
      const params = new URLSearchParams();
      if (filters.medewerker) params.append('medewerker', filters.medewerker);
      params.append('dayOffset', dayAgendaOffset);

      const response = await axios.get(`${API_BASE_URL}/dashboard/vandaag?${params.toString()}`);
      if (requestId !== dayRequestRef.current) return;
      if (response.data?.success) {
        setDayAgendaItems(response.data.data ?? []);
      } else {
        throw new Error('Ongeldig antwoord');
      }
    } catch (error) {
      if (requestId !== dayRequestRef.current) return;
      setDayAgendaError('Controleer je verbinding en probeer het opnieuw.');
      setDayAgendaItems([]);
    } finally {
      if (requestId === dayRequestRef.current) setDayAgendaLoading(false);
    }
  };

  const handleRefreshData = async ({ force = false, onComplete } = {}) => {
    if (!isAuthenticated && !force) return;
    try {
      setRefreshing(true);
      setMessage('');
      if (ENABLE_AGENDA_SYNC) {
        await axios.post(`${API_BASE_URL}/ververs-data`);
      }
      await Promise.all([fetchAllDashboards(), fetchVandaagDashboard(), fetchMedewerkers(), fetchEmployeeDirectory(),
        ...(viewMode === 'agenda' ? [fetchAgendaItems()] : []),
        ...(viewMode === 'dagagenda' ? [fetchDayAgendaItems()] : [])]);
      setMessage('Agenda bijgewerkt');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setMessage('Fout bij verversen van data');
    } finally {
      setRefreshing(false);
      if (typeof onComplete === 'function') {
        onComplete();
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    const formattedCode = loginForm.code.trim().toUpperCase();

    if (!formattedCode) {
      setLoginError('Voer een medewerkercode in');
      return;
    }

    // Trim password to handle any accidental whitespace
    const trimmedPassword = loginForm.password.trim();

    if (!trimmedPassword) {
      setLoginError('Voer een wachtwoord in');
      return;
    }

    try {
      // Call login API endpoint
      const response = await axios.post(`${API_BASE_URL}/login`, {
        code: formattedCode,
        password: trimmedPassword
      });

      if (response.data.success && response.data.token) {
        const authState = {
          code: response.data.user?.code || formattedCode,
          isAdmin: Boolean(response.data.user?.isAdmin)
        };
        setAuth(authState);
        setFilters(prev => ({
          ...prev,
          medewerker: ''
        }));
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(authState));
          window.localStorage.setItem(TOKEN_STORAGE_KEY, response.data.token);
        }
        setLoginForm({ code: '', password: '' });
        setLoginError('');
        if (ENABLE_AGENDA_SYNC) {
          setInitializing(true);
          handleRefreshData({ force: true, onComplete: () => setInitializing(false) });
        } else {
          setInitializing(false);
        }
      } else {
        setLoginError('Login mislukt');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.data?.message) {
        setLoginError(error.response.data.message);
      } else {
        setLoginError('Login mislukt. Probeer het opnieuw.');
      }
    }
  };

  const handleLogout = () => {
    setAuth(null);
    setFilters({ medewerker: '', weekOffset: 0 });
    setDashboardData({
      vandaag: [],
      morgen: [],
      vandaagVGK: [],
      weekOL: [],
      klantenWeek: [],
      vandaagDMG2: [],
      afwezigWeek: [],
      eigenVolgendeWeek: []
    });
    setMedewerkers([]);
    setVandaagOffset(0);
    setShowMedewerkerPanel(false);
    setInitializing(false);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  };

  const handleWeekNavigation = (direction) => {
    scrollPositionRef.current = window.scrollY;
    setFilters(prev => ({ ...prev, weekOffset: prev.weekOffset + direction }));
  };

  const handleThemeToggle = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const handleAgendaItemSelect = (item) => {
    if (!item) return;
    setSelectedAgendaItem(item);
  };

  const handleAgendaItemDetailClose = () => {
    setSelectedAgendaItem(null);
  };

  const handleKlantInfoClick = (bedrijf) => {
    if (bedrijf && bedrijf.trim()) {
      setSelectedAgendaItem(null);
      navigate(`/klant/${encodeURIComponent(bedrijf.trim())}`);
    }
  };

  const getWeekNumber = (weekOffset = 0) => {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)); // Monday of the target week
    
    // ISO week number calculation
    const d = new Date(Date.UTC(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    
    return weekNumber;
  };

  const getDateForOffset = (offset = 0) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + offset);
    return date;
  };

  const formatLongDate = (date) => {
    const formatter = new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
    let formatted = formatter.format(date);
    formatted = formatted.replace(/\.$/, '');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const getVandaagTitle = () => {
    const targetDate = getDateForOffset(vandaagOffset);
    const today = getDateForOffset(0);
    const tomorrow = getDateForOffset(1);

    const isSameDay = (a, b) => a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();

    if (isSameDay(targetDate, today)) {
      return 'Vandaag';
    }

    if (isSameDay(targetDate, tomorrow)) {
      return 'Morgen';
    }

    return formatLongDate(targetDate);
  };

  const vandaagTitle = getVandaagTitle();
  const vandaagSubtitle = formatLongDate(getDateForOffset(vandaagOffset));
  const morgenSubtitle = formatLongDate(getDateForOffset(1));
  const vandaagDatumLabel = formatLongDate(getDateForOffset(0));
  const weekNumber = getWeekNumber(filters.weekOffset);
  const weekLabelDisplay = `Week ${weekNumber}`;
  const isDarkMode = theme === 'dark';
  const themeToggleLabel = isDarkMode ? 'Schakel naar lichte modus' : 'Schakel naar donkere modus';

  const getWeekLabel = () => {
    const weekNumber = getWeekNumber(filters.weekOffset);
    if (filters.weekOffset === 0) return `Nu (week ${weekNumber})`;
    if (filters.weekOffset === -1) return `Vorig (week ${weekNumber})`;
    if (filters.weekOffset === 1) return `Volgend (week ${weekNumber})`;
    return filters.weekOffset < 0 
      ? `${Math.abs(filters.weekOffset)} weken terug (week ${weekNumber})` 
      : `${filters.weekOffset} weken vooruit (week ${weekNumber})`;
  };

  const formatAgendaDayLabel = (dateKey) => {
    if (!dateKey) return '';
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) return '';
    const date = new Date(year, month - 1, day);
    const formatter = new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    const formatted = formatter.format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const agendaDays = useMemo(() => {
    if (!agendaItems || agendaItems.length === 0) return [];

    const groups = new Map();

    agendaItems.forEach((item) => {
      const dateKey = getItemDateKey(item);
      if (!dateKey) return;

      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey).push(item);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const [aYear, aMonth, aDay] = a.split('-').map(Number);
        const [bYear, bMonth, bDay] = b.split('-').map(Number);
        return new Date(aYear, aMonth - 1, aDay) - new Date(bYear, bMonth - 1, bDay);
      })
      .map(([dateKey, items]) => ({
        dateKey,
        label: formatAgendaDayLabel(dateKey),
        items: items
          .slice()
          .sort(
            (a, b) =>
              (getItemStartDate(a)?.getTime() ?? 0) -
              (getItemStartDate(b)?.getTime() ?? 0)
          )
      }));
  }, [agendaItems]);

  const renderLoginScreen = () => (
    <div className="App login-screen modern-login">
      <button
        type="button"
        className={`login-theme-toggle-floating view-switcher-icon-button theme-toggle-button ${isDarkMode ? 'active' : ''}`}
        onClick={handleThemeToggle}
        aria-pressed={isDarkMode}
        aria-label={themeToggleLabel}
        title={themeToggleLabel}
      >
        {isDarkMode ? (
          <svg className="theme-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12zm0-16a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1zm0 18a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1zm10-8a1 1 0 0 1-1 1h-2a1 1 0 1 1 0-2h2a1 1 0 0 1 1 1zm-16 0a1 1 0 0 1-1 1H3a1 1 0 1 1 0-2h2a1 1 0 0 1 1 1zm12.95-7.05a1 1 0 0 1 0 1.41L18.34 7a1 1 0 1 1-1.41-1.41l1.61-1.61a1 1 0 0 1 1.41 0zM7.07 16.93a1 1 0 0 1 0 1.41L5.46 19.95a1 1 0 0 1-1.41-1.41l1.61-1.61a1 1 0 0 1 1.41 0zm0-11.32L5.46 4a1 1 0 0 1 1.41-1.41L8.48 4.2A1 1 0 0 1 7.07 5.61zm11.32 11.32 1.61 1.61a1 1 0 0 1-1.41 1.41l-1.61-1.61a1 1 0 1 1 1.41-1.41z" />
          </svg>
        ) : (
          <svg className="theme-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 12.79A9 9 0 0 1 11.21 3a7 7 0 1 0 9.79 9.79z" />
          </svg>
        )}
      </button>
      <section className="login-story" aria-label="Werkagenda introductie">
        <div className="story-brand"><img src={logo} alt="" /><strong>Werkagenda</strong></div>
        <div className="story-copy"><span className="story-eyebrow">ALLES OP ÉÉN PLEK</span><h1>Jouw werkdag.<br /><em>In één oogopslag.</em></h1><p>Bekijk je afspraken, houd je team bij en begin iedere dag met overzicht.</p></div>
        <div className="story-visual" aria-hidden="true">
          <div className="story-orbit orbit-one" /><div className="story-orbit orbit-two" />
          <div className="story-calendar"><div className="story-calendar-top"><span className="story-dot" /><span className="story-dot" /><span className="story-dot" /><span className="story-month">NOVEMBER</span></div><div className="story-weekdays"><span>M</span><span>D</span><span>W</span><span>D</span><span>V</span><span>Z</span><span>Z</span></div><div className="story-days">{Array.from({ length: 35 }, (_, index) => <span key={index} className={`${[9, 10, 15, 16, 17, 23].includes(index) ? 'marked' : ''} ${index === 16 ? 'today' : ''}`}>{index < 6 || index > 35 ? '' : index - 5}</span>)}</div></div>
          <div className="story-float float-appointment"><span className="story-float-icon">◷</span><span><strong>Nieuwe afspraak</strong><small>09:00 · Planningsoverleg</small></span></div>
          <div className="story-float float-team"><span className="story-avatars"><i>EM</i><i>JW</i><i>PS</i></span><span><strong>Team vandaag</strong><small>Samen aan de slag</small></span></div>
        </div>
        <div className="story-footer"><span>Meer overzicht. Minder zoeken. Een betere werkdag.</span><span className="story-credit">By I-TeQ B.V.</span></div>
      </section>
      <div className="login-card">
        <header className="login-header">
          <div className="login-mobile-brand"><img src={logo} alt="" /><strong>Werkagenda</strong></div>
          <span className="login-eyebrow">WELKOM TERUG</span>
          <h2>Goed je weer te zien.</h2>
          <p>Log in en pak de draad weer op.</p>
        </header>
        <form
          className="login-form"
          onSubmit={handleLoginSubmit}
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
        >
          <label className="login-field">
            <span>Medewerkercode</span>
            <input
              type="text"
              value={loginForm.code}
              onChange={(event) => {
                setLoginForm((prev) => ({ ...prev, code: event.target.value }));
                setLoginError('');
              }}
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
            />
          </label>
          <label className="login-field">
            <span>Wachtwoord</span>
            <input
              type="password"
              value={loginForm.password}
              onChange={(event) => {
                setLoginForm((prev) => ({ ...prev, password: event.target.value }));
                setLoginError('');
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
            />
          </label>
          {loginError && <div className="login-error">{loginError}</div>}
          <button type="submit" className="login-button">
            Inloggen
          </button>
        </form>
      </div>
    </div>
  );

  const renderInitializingScreen = () => (
    <div className="App initializing-screen">
      <div className="initializing-card">
        <div className="initializing-spinner" aria-hidden="true" />
        <p className="initializing-text">Data wordt ververst...</p>
      </div>
    </div>
  );

  const renderDashboardView = () => (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="header-top-row">
            <img src={logo} alt="Werk Agenda" className="header-logo" />
            <div className="header-top-right">
              {viewMode === 'dagagenda' ? <span className="dayplan-toolbar-label">Dagplanning</span> : <div className="header-week-controls" aria-label={getWeekLabel()}>
                <button 
                  className="header-week-button"
                  onClick={() => handleWeekNavigation(-1)}
                  type="button"
                  aria-label="Vorige week"
                >
                  -
                </button>
                <span className="header-week-label" aria-live="polite">
                  {weekLabelDisplay}
                </span>
                <button 
                  className="header-week-button"
                  onClick={() => handleWeekNavigation(1)}
                  type="button"
                  aria-label="Volgende week"
                >
                  +
                </button>
              </div>}
              <div className="header-actions">
                <button
                  type="button"
                  className={`filters-toggle ${showMedewerkerPanel ? 'active' : ''}`}
                  onClick={() => setShowMedewerkerPanel(prev => !prev)}
                  aria-expanded={showMedewerkerPanel}
                  aria-controls="medewerker-panel"
                  title="Medewerkers filter"
                >
                  <svg
                    className="header-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" />
                  </svg>
                </button>
                <button 
                  className="refresh-button" 
                  onClick={handleRefreshData}
                  disabled={refreshing}
                  type="button"
                  title="Ververs data"
                >
                  <svg
                    className="header-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7c2.76 0 5 2.24 5 5a5 5 0 0 1-8.9 3h-2.1a7 7 0 0 0 12 0 7 7 0 0 0-.35-8.65z" />
                  </svg>
                </button>

              </div>
            </div>
          </div>
        </div>
        <div 
          id="medewerker-panel"
          className={`medewerker-panel ${showMedewerkerPanel ? 'open' : ''}`}
          aria-hidden={!showMedewerkerPanel}
        >
          <div className="medewerker-panel-content">
            {medewerkers.length === 0 ? (
              <span className="medewerker-panel-empty">Geen medewerkers beschikbaar</span>
            ) : (
              medewerkers.map((medewerker) => (
                <button
                  key={medewerker}
                  className={`medewerker-button ${filters.medewerker === medewerker ? 'active' : ''}`}
                  onClick={() => handleFilterChange('medewerker', filters.medewerker === medewerker ? '' : medewerker)}
                >
                  {medewerker}
                </button>
              ))
            )}
          </div>
        </div>
      </header>
      
      <div className="container">
        {viewMode === 'dashboards' ? (
          loading.all ? (
            <div className="loading">Laden...</div>
          ) : (
            <div className="dashboard-grid">
              <DashboardWidget
                title={vandaagTitle}
                subtitle={vandaagSubtitle}
                items={dashboardData.vandaag}
                type="agenda"
                className="widget-vandaag"
                activeMedewerkerCode={auth?.code ?? ''}
                actions={
                  <div className="vandaag-actions">
                    <button
                      type="button"
                      className="vandaag-button"
                      onClick={() => setVandaagOffset(prev => prev - 1)}
                      aria-label="Vorige dag"
                    >
                      -
                    </button>
                    <button
                      type="button"
                      className="vandaag-button vandaag-reset"
                      onClick={() => setVandaagOffset(0)}
                      aria-label="Ga naar vandaag"
                    >
                      Vandaag
                    </button>
                    <button
                      type="button"
                      className="vandaag-button"
                      onClick={() => setVandaagOffset(prev => prev + 1)}
                      aria-label="Volgende dag"
                    >
                      +
                    </button>
                  </div>
                }
                onAgendaItemSelect={handleAgendaItemSelect}
              />
              
              <DashboardWidget
                title="Morgen"
                subtitle={morgenSubtitle}
                items={dashboardData.morgen}
                type="agenda"
                className="widget-morgen"
                activeMedewerkerCode={auth?.code ?? ''}
                onAgendaItemSelect={handleAgendaItemSelect}
              />
              
              <DashboardWidget
                title="Vandaag in VGK"
                subtitle={vandaagDatumLabel}
                items={dashboardData.vandaagVGK}
                type="agenda"
                className="widget-vandaag-vgk"
                activeMedewerkerCode={auth?.code ?? ''}
                onAgendaItemSelect={handleAgendaItemSelect}
              />
              
              <DashboardWidget
                title={`Op locatie week ${weekNumber}`}
                subtitle="(alle items met locatie OL)"
                items={dashboardData.weekOL}
                type="agenda"
                className="widget-week-ol"
                activeMedewerkerCode={auth?.code ?? ''}
                onAgendaItemSelect={handleAgendaItemSelect}
              />
              
              <DashboardWidget
                title={`Klanten week ${weekNumber}`}
                subtitle="Deze klanten komen voor deze week"
                items={dashboardData.klantenWeek}
                type="klanten"
                className="widget-klanten-week"
                activeMedewerkerCode={auth?.code ?? ''}
                onKlantClick={handleKlantInfoClick}
              />
              
              <DashboardWidget
                title="Afwezigen"
                subtitle="Deze medewerkers zijn afwezig deze week"
                items={dashboardData.afwezigWeek}
                type="afwezig"
                className="widget-afwezig-week"
                activeMedewerkerCode={auth?.code ?? ''}
              />
              
              <DashboardWidget
                title="Vandaag in DMG"
                subtitle={vandaagDatumLabel}
                items={dashboardData.vandaagDMG2}
                type="agenda"
                className="widget-vandaag-dmg"
                activeMedewerkerCode={auth?.code ?? ''}
                onAgendaItemSelect={handleAgendaItemSelect}
              />
              
              <DashboardWidget
                title="Eigen, volgende week"
                subtitle={`Week ${getWeekNumber(1)}`}
                items={dashboardData.eigenVolgendeWeek}
                type="agenda"
                className="widget-eigen-volgende-week"
                activeMedewerkerCode={auth?.code ?? ''}
                onAgendaItemSelect={handleAgendaItemSelect}
              />
            </div>
          )
        ) : viewMode === 'agenda' ? (
          agendaLoading ? (
            <div className="loading">Laden...</div>
          ) : (
            <AgendaList
              days={agendaDays}
              activeMedewerkerCode={auth?.code ?? ''}
              onAgendaItemSelect={handleAgendaItemSelect}
            />
          )
        ) : viewMode === 'dagagenda' ? (
            <DayAgendaView
              items={dayAgendaItems}
              dateLabel={formatLongDate(getDateForOffset(dayAgendaOffset))}
              activeMedewerkerCode={auth?.code ?? ''}
              onAgendaItemSelect={handleAgendaItemSelect}
              onPreviousDay={() => setDayAgendaOffset(prev => prev - 1)}
              onToday={() => setDayAgendaOffset(0)}
              onNextDay={() => setDayAgendaOffset(prev => prev + 1)}
              isToday={dayAgendaOffset === 0}
              loading={dayAgendaLoading}
              error={dayAgendaError}
              onRetry={fetchDayAgendaItems}
              employeeFilter={filters.medewerker}
            />
        ) : viewMode === 'beheer' && auth?.isAdmin ? (
          <MedewerkerCodesAdminPage apiBaseUrl={API_BASE_URL} accountCode={auth?.code} />
        ) : null}
      </div>
      {message && <div className="prototype-toast" role="status">{message}</div>}
      {selectedAgendaItem && (
        <AgendaItemDetailModal
          item={selectedAgendaItem}
          onClose={handleAgendaItemDetailClose}
          activeMedewerkerCode={auth?.code ?? ''}
          onKlantInfoClick={handleKlantInfoClick}
        />
      )}
    </div>
  );

  const renderHomeRoute = () => {
    if (!isAuthenticated) {
      return renderLoginScreen();
    }
    if (initializing) {
      return renderInitializingScreen();
    }
    if (viewMode === 'beheer' && auth?.isAdmin) return <main className="prototype-page"><MedewerkerCodesAdminPage apiBaseUrl={API_BASE_URL} accountCode={auth?.code} /></main>;
    if (viewMode === 'nieuw') return <NewAgendaItem apiBaseUrl={API_BASE_URL} employees={employeeDirectory} loading={directoryLoading} error={directoryError} onRetry={fetchEmployeeDirectory} onCancel={() => setViewMode('agenda')} />;
    if (viewMode === 'profiel') return <main className="prototype-page"><header className="page-heading"><div><span className="page-eyebrow">ACCOUNT</span><h1>Profiel</h1><p>Je gegevens en voorkeuren.</p></div></header><section className="form-card profile-card"><span className="profile-avatar">{auth?.code?.slice(0, 2)}</span><h2>{auth?.code}</h2><dl><dt>Medewerkercode</dt><dd>{auth?.code}</dd><dt>Rol</dt><dd>{auth?.isAdmin ? 'Beheerder' : 'Medewerker'}</dd><dt>Weergave</dt><dd>{isDarkMode ? 'Donker' : 'Licht'}</dd></dl><button className="secondary-button" onClick={handleThemeToggle}>{themeToggleLabel}</button></section></main>;
    return renderDashboardView();
  };

  const renderCustomerRoute = () => {
    if (!isAuthenticated) {
      return renderLoginScreen();
    }
    if (initializing) {
      return renderInitializingScreen();
    }
    return (
      <CustomerInfoPage apiBaseUrl={API_BASE_URL} />
    );
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (!body) return;

    if (selectedAgendaItem) {
      body.classList.add('body-scroll-lock-active');
    } else {
      body.classList.remove('body-scroll-lock-active');
    }

    return () => {
      body.classList.remove('body-scroll-lock-active');
    };
  }, [selectedAgendaItem]);

  if (MAINTENANCE_MODE && isAuthenticated) {
    return (
      <main className="maintenance-screen">
        <section className="maintenance-card" role="status">
          <h1>Offline door onderhoud</h1>
          <p>De werkagenda is tijdelijk niet beschikbaar. Probeer het later opnieuw.</p>
        </section>
      </main>
    );
  }

  const routes = <Routes>
    <Route path="/klant/:bedrijfCode" element={renderCustomerRoute()} />
    <Route path="*" element={renderHomeRoute()} />
  </Routes>;
  return isAuthenticated ? <Workspace view={viewMode} code={auth?.code} isAdmin={auth?.isAdmin} isDark={isDarkMode} onTheme={handleThemeToggle} onLogout={handleLogout} onNavigate={value => { setViewMode(value); navigate('/'); }}>{routes}</Workspace> : routes;
}

function MedewerkerCodesAdminPage({ apiBaseUrl, accountCode }) {
  const [adminTab, setAdminTab] = useState('codes');
  const storageKey = `werkagenda-prototype-admin:${accountCode}`;
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const fetchCodes = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError('');
      const saved = window.localStorage.getItem(storageKey);
      if (!reset && saved) {
        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed)) throw new Error('Ongeldige lokale kopie');
        setCodes(parsed);
      } else {
        const response = await axios.get(`${apiBaseUrl}/admin/medewerker-codes`);
        if (!response.data.success || !Array.isArray(response.data.data)) throw new Error('Ongeldig antwoord');
        const initial = response.data.data.map(row => ({ ...row, is_active: Boolean(Number(row.is_active)) }));
        window.localStorage.setItem(storageKey, JSON.stringify(initial));
        setCodes(initial);
        if (reset) setNotice('Lokale wijzigingen gewist. De live codes zijn opnieuw ingelezen.');
      }
    } catch {
      setError('Codes laden is mislukt. Controleer je verbinding en browseropslag.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, storageKey]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const saveLocal = (next, message) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      setCodes(next);
      setError('');
      setNotice(message);
      return true;
    } catch {
      setError('Lokaal opslaan is mislukt. Controleer of browseropslag is toegestaan.');
      return false;
    }
  };

  const handleAddCode = event => {
    event.preventDefault();
    if (loading) return;
    const code = newCode.trim().toUpperCase();
    if (!code) { setError('Vul een medewerkercode in'); return; }
    if (codes.some(row => row.code.toUpperCase() === code)) { setError('Deze medewerkercode bestaat al'); return; }
    if (saveLocal([...codes, { code, is_active: true, login_count: 0 }].sort((a, b) => a.code.localeCompare(b.code)), `${code} is lokaal toegevoegd`)) setNewCode('');
  };

  const handleToggleActive = row => saveLocal(
    codes.map(code => code.code === row.code ? { ...code, is_active: !row.is_active } : code),
    `${row.code} is lokaal ${row.is_active ? 'inactief' : 'actief'} gezet`
  );
  const handleDeleteCode = row => saveLocal(codes.filter(code => code.code !== row.code), `${row.code} is lokaal verwijderd`);

  return (
    <section className="admin-page" aria-label="Beheer">
      <div className="admin-tabs" role="tablist" aria-label="Beheer onderdelen"><button type="button" role="tab" aria-selected={adminTab === 'codes'} onClick={() => setAdminTab('codes')}>Medewerkercodes</button><button type="button" role="tab" aria-selected={adminTab === 'history'} onClick={() => setAdminTab('history')}>Inloghistorie</button></div>
      {adminTab === 'history' ? <LoginHistory apiBaseUrl={apiBaseUrl} /> : <div role="tabpanel" aria-label="Medewerkercodes">
      <div className="admin-header">
        <h1>Medewerkercodes</h1>
        <button type="button" className="admin-refresh-button" onClick={() => fetchCodes()} disabled={loading}>
          Verversen
        </button>
      </div>

      <p className="admin-message">Lokale proefversie: wijzigingen worden alleen in deze browser bewaard. De live toegangsrechten blijven ongewijzigd.</p>
      <button type="button" className="secondary-button" disabled={loading} onClick={() => fetchCodes(true)}>
        Lokale wijzigingen wissen en live codes herladen
      </button>
      <form className="admin-code-form" onSubmit={handleAddCode}>
        <label className="admin-code-field">
          <span>Nieuwe code</span>
          <input
            type="text"
            value={newCode}
            onChange={(event) => {
              setNewCode(event.target.value);
              setError('');
              setNotice('');
            }}
            autoComplete="off"
          />
        </label>
        <button type="submit" className="admin-save-button" disabled={loading}>
          Toevoegen
        </button>
      </form>

      {error && <div className="admin-message error">{error}</div>}
      {notice && <div className="admin-message success">{notice}</div>}

      {loading ? (
        <div className="loading">Laden...</div>
      ) : (
        <div className="admin-code-list">
          {codes.length === 0 ? (
            <div className="admin-empty">Geen medewerkercodes gevonden</div>
          ) : (
            codes.map((code) => (
              <div key={code.id ?? code.code} className="admin-code-row">
                <div className="admin-code-main">
                  <span className="admin-code-value">{code.code}</span>
                  <span className={`admin-code-status ${code.is_active ? 'active' : 'inactive'}`}>
                    {code.is_active ? 'Actief' : 'Inactief'}
                  </span>
                  <span className="admin-code-login-count">
                    {Number(code.login_count || 0)}x ingelogd
                  </span>
                </div>
                <div className="admin-code-actions">
                  <button
                    type="button"
                    className="admin-row-button"
                    disabled={loading}
                    onClick={() => handleToggleActive(code)}
                  >
                    {code.is_active ? 'Deactiveren' : 'Activeren'}
                  </button>
                  <button
                    type="button"
                    className="admin-row-button danger"
                    disabled={loading}
                    onClick={() => handleDeleteCode(code)}
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      </div>}
    </section>
  );
}

function DashboardWidget({
  title,
  subtitle,
  items,
  type,
  actions,
  className = '',
  activeMedewerkerCode = '',
  onAgendaItemSelect,
  onKlantClick
}) {
  return (
    <div className={`dashboard-widget ${className}`.trim()}>
      <div className="widget-header">
        <div className="widget-header-text">
          <h3>{title}</h3>
          {subtitle && <p className="widget-subtitle">{subtitle}</p>}
        </div>
        {actions && (
          <div className="widget-actions">
            {actions}
          </div>
        )}
      </div>
      <div className="widget-content">
        {type === 'agenda' ? (
          items.length === 0 ? (
            <div className="empty-widget">Geen items</div>
          ) : (
            items.map((item) => (
              <CompactAgendaItem
                key={item.id}
                item={item}
                activeMedewerkerCode={activeMedewerkerCode}
                onSelect={onAgendaItemSelect}
              />
            ))
          )
        ) : type === 'klanten' ? (
          items.length === 0 ? (
            <div className="empty-widget">Geen klanten</div>
          ) : (
            <div className="klanten-grid">
              {items.map((klant, index) => (
                <div 
                  key={index} 
                  className="klant-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (typeof onKlantClick === 'function' && klant.klant) {
                      onKlantClick(klant.klant);
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && typeof onKlantClick === 'function' && klant.klant) {
                      e.preventDefault();
                      onKlantClick(klant.klant);
                    }
                  }}
                >
                  <span className="klant-naam">{klant.klant}</span>
                  <span className="klant-aantal">{klant.aantal}</span>
                </div>
              ))}
            </div>
          )
        ) : (
          items.length === 0 ? (
            <div className="empty-widget">Geen afwezigen</div>
          ) : (
            <div className="afwezig-grid">
              {items.map((item, index) => {
                const medewerker = extractMedewerkerCodes(item).join(', ') || '-';
                const dateStr = (() => {
                  const date = parseUTCDate(item.datum);
                  if (!date) return '-';
                  const day = String(date.getUTCDate()).padStart(2, '0');
                  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                  return `${day}-${month}`;
                })();
                const durationHours = (() => {
                  const start = item.actie_tijd_start ? parseUTCDate(item.actie_tijd_start) : null;
                  const stop = item.actie_tijd_stop ? parseUTCDate(item.actie_tijd_stop) : null;
                  if (!start || !stop) return '0.0u';
                  // Calculate duration using UTC time methods
                  const ms = stop.getTime() - start.getTime();
                  let hours = Math.max(0, ms / 3600000);
                  // Display tweak: force 8.5h to show as 8.0h
                  if (Math.abs(hours - 8.5) < 0.01) {
                    hours = 8.0;
                  }
                  return `${hours.toFixed(1).replace('.', ',')}u`;
                })();
                return (
                  <div key={index} className="afwezig-item">
                    <span className="afwezig-medewerker">{medewerker}</span>
                    <span className="afwezig-datum">{dateStr}</span>
                    <span className="afwezig-uren">{durationHours}</span>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function CompactAgendaItem({ item, activeMedewerkerCode = '', onSelect }) {
  // Get dates directly - they now contain full date+time
  const startDate = getItemStartDate(item);
  const stopDate = getItemStopDate(item);
  const datumDate = parseUTCDate(item.datum ?? item.Datum);

  const formatDate = (date) => {
    if (!date) return '';
    // Use UTC methods to get the date parts (from UTC string)
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${day}-${month}`;
  };

  const formatTime = (date) => {
    if (!date) return '';
    // Use UTC methods to get the time parts (from UTC string)
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const normalizedActiveCode = activeMedewerkerCode?.trim().toUpperCase() ?? '';

  const medewerkerCodes = useMemo(
    () => extractMedewerkerCodes(item),
    [item]
  );

  const hasActiveMedewerker = normalizedActiveCode
    ? isCurrentMedewerker(medewerkerCodes, normalizedActiveCode)
    : false;

  const locatieRaw = (item.locatie ?? item.Locatie ?? '').toString().trim().toUpperCase();
  const locatieEmoji = (() => {
    switch (locatieRaw) {
      case 'OL':
        return { emoji: '➡️', label: 'Op locatie' };
      case 'AFW':
        return null;
      case 'NS':
        return { emoji: '🛑', label: 'Niet beschikbaar' };
      default:
        return null;
    }
  })();
  const isVerLocatie = locatieRaw === 'VER';
  const isAfwLocatie = locatieRaw === 'AFW';
  const handleSelect = () => {
    if (typeof onSelect === 'function') {
      onSelect(item);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  const startTimeText = formatTime(startDate);
  const stopTimeText = formatTime(stopDate);
  const displayDateText = formatDate(datumDate ?? startDate);

  const renderedMedewerkerList = useMemo(() => {
    const elements = [];
    medewerkerCodes.forEach((code, index) => {
      if (index > 0) {
        elements.push(
          <span key={`sep-${code}-${index}`} className="compact-medewerker-separator">
            ,{' '}
          </span>
        );
      }

      const isCurrent = isCurrentMedewerker([code], normalizedActiveCode);
      elements.push(
        isCurrent ? (
          <span key={`code-${code}`} className="compact-medewerker-code is-current">
            {code}
          </span>
        ) : (
          <span key={`code-${code}`} className="compact-medewerker-text">
            {code}
          </span>
        )
      );
    });
    return elements;
  }, [medewerkerCodes, normalizedActiveCode]);

  return (
    <div
      className="compact-agenda-item"
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <div className="compact-item-header">
        <span className="compact-date">{displayDateText}</span>
        <div className="compact-header-meta">
          {locatieRaw && (
            <span
              className={`compact-location-code${isVerLocatie ? ' is-ver' : ''}${isAfwLocatie ? ' is-afw' : ''}`}
              title={`Locatie ${locatieRaw}`}
            >
              {locatieRaw}
            </span>
          )}
          {locatieEmoji && (
            <span className="compact-location-indicator" role="img" aria-label={locatieEmoji.label}>
              {locatieEmoji.emoji}
            </span>
          )}
          <span className="compact-time">
            {startTimeText}
            {stopTimeText ? ` - ${stopTimeText}` : ''}
          </span>
        </div>
      </div>
      <div className="compact-item-body">
        <div className="compact-omschrijving">{item.actie_omschrijving || '-'}</div>
        <div className="compact-klant">
          {item.bedrijf || '-'}
          {medewerkerCodes.length > 0 && (
            <>
              {' '}
              <span className={`compact-medewerker${hasActiveMedewerker ? ' has-active' : ''}`}>
                <span className="compact-medewerker-paren">(</span>
                <span className="compact-medewerker-list">
                  {renderedMedewerkerList}
                </span>
                <span className="compact-medewerker-paren">)</span>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AgendaList({ days, activeMedewerkerCode = '', onAgendaItemSelect }) {
  if (!days || days.length === 0) {
    return (
      <div className="agenda-empty">
        Geen agenda items voor de geselecteerde periode
      </div>
    );
  }

  return (
    <div className="agenda-view">
      {days.map((day) => (
        <section key={day.dateKey} className="agenda-day">
          <div className="agenda-day-header">
            <span className="agenda-day-label">{day.label}</span>
            <span className="agenda-day-count">
              {day.items.length} {day.items.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          <div className="agenda-day-items">
            {day.items.map((item) => (
              <CompactAgendaItem
                key={item.id ?? `${day.dateKey}-${item.actie_tijd_start}`}
                item={item}
                activeMedewerkerCode={activeMedewerkerCode}
                onSelect={onAgendaItemSelect}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function DayAgendaView({ items, dateLabel, activeMedewerkerCode = '', onAgendaItemSelect,
  onPreviousDay, onToday, onNextDay, isToday, loading, error, onRetry, employeeFilter }) {
  const [search, setSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const activeCode = activeMedewerkerCode.trim().toUpperCase();
  const time = date => date ? `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}` : '';
  const mine = item => isCurrentMedewerker(extractMedewerkerCodes(item), activeCode);
  const isAbsence = item => ['AFW', 'VER'].includes((item.locatie || '').trim().toUpperCase());
  const query = search.trim().toLowerCase();
  const filtered = (items || []).filter(item => (!mineOnly || mine(item)) &&
    [item.actie_omschrijving, item.bedrijf, item.relatie_naam, item.locatie,
      ...(item.medewerkers || []).flatMap(m => [m.code, m.volledige_naam]), item.medewerker_veld]
      .filter(Boolean).join(' ').toLowerCase().includes(query))
    .sort((a, b) => (getItemStartDate(a)?.getTime() ?? Infinity) - (getItemStartDate(b)?.getTime() ?? Infinity));
  const groups = [
    { label: 'Ochtend', hint: 'Voor 12:00', match: item => getItemStartDate(item)?.getUTCHours() < 12 },
    { label: 'Middag', hint: '12:00 – 18:00', match: item => { const h = getItemStartDate(item)?.getUTCHours(); return h >= 12 && h < 18; } },
    { label: 'Avond', hint: 'Vanaf 18:00', match: item => getItemStartDate(item)?.getUTCHours() >= 18 },
    { label: 'Zonder begintijd', hint: 'Tijd nog niet ingevuld', match: item => !getItemStartDate(item) },
    { label: 'Afwezig & verlof', hint: 'Beschikbaarheid van het team', absence: true, match: isAbsence }
  ].map(group => ({ ...group, items: filtered.filter(item => group.absence ? group.match(item) : !isAbsence(item) && group.match(item)) })).filter(group => group.items.length);
  const appointmentCount = filtered.filter(item => !isAbsence(item)).length;
  const absenceCount = filtered.length - appointmentCount;
  return <section className="dayplan" aria-label="Dagagenda">
    <header className="dayplan-heading">
      <div><span className="page-eyebrow">JOUW PLANNING</span><h1>Dagagenda</h1><p className="dayplan-date" aria-live="polite">{dateLabel}{isToday && <span>Vandaag</span>}</p></div>
      <div className="dayplan-navigation" role="group" aria-label="Dag kiezen">
        <button type="button" aria-label="Vorige dag" onClick={onPreviousDay} disabled={loading}>←</button>
        <button type="button" onClick={onToday} disabled={loading || isToday}>Vandaag</button>
        <button type="button" aria-label="Volgende dag" onClick={onNextDay} disabled={loading}>→</button>
      </div>
    </header>
    <div className="dayplan-tools">
      <div className="dayplan-scope" role="group" aria-label="Afspraken tonen"><button type="button" aria-pressed={!mineOnly} onClick={() => setMineOnly(false)}>Alle afspraken</button><button type="button" aria-pressed={mineOnly} onClick={() => setMineOnly(true)}>Mijn afspraken</button></div>
      <label className="dayplan-search"><span className="sr-only">Zoeken in deze dag</span><input type="search" placeholder="Zoek afspraak, klant of medewerker…" value={search} onChange={e => setSearch(e.target.value)} /></label>
    </div>
    {employeeFilter && <p className="dayplan-filter-note">Medewerkerfilter: <strong>{employeeFilter}</strong>. Dit filter geldt ook voor dit dagoverzicht.</p>}
    {!loading && !error && <p className="dayplan-summary" role="status">{appointmentCount} {appointmentCount === 1 ? 'afspraak' : 'afspraken'}{absenceCount > 0 && ` · ${absenceCount} afwezig / verlof`}{(query || mineOnly) && ' in deze selectie'}</p>}
    {loading ? <div className="dayplan-empty" role="status"><h2>Dagplanning laden…</h2><p>De afspraken worden opgehaald.</p></div> : error ? <div className="dayplan-empty" role="alert"><h2>De dagplanning kon niet worden geladen</h2><p>{error}</p><button type="button" className="secondary-button" onClick={onRetry}>Opnieuw proberen</button></div> : !filtered.length ? <div className="dayplan-empty"><span aria-hidden="true">▤</span><h2>{query || mineOnly ? 'Geen afspraken gevonden' : 'Geen afspraken voor deze dag'}</h2><p>{query || mineOnly ? 'Pas je zoekopdracht aan of toon alle afspraken.' : 'Kies een andere dag om de planning te bekijken.'}</p>{(query || mineOnly) && <button type="button" className="secondary-button" onClick={() => { setSearch(''); setMineOnly(false); }}>Zoekopdracht en selectie wissen</button>}</div> :
      <div className="dayplan-groups">{groups.map(group => <section key={group.label} className={`dayplan-group ${group.absence ? 'is-absence' : ''}`} aria-label={group.label}>
        <div className="dayplan-group-heading"><h2>{group.label} <span>{group.items.length}</span></h2><p>{group.hint}</p></div>
        <div className="dayplan-cards">{group.items.map(item => {
          const start = getItemStartDate(item), stop = getItemStopDate(item);
          const codes = extractMedewerkerCodes(item);
          return <button type="button" className={`dayplan-card ${mine(item) ? 'is-mine' : ''}`} key={item.id ?? item.id_agenda} onClick={() => onAgendaItemSelect(item)}>
            <span className="dayplan-time"><strong>{time(start) || 'Tijd volgt'}</strong>{stop && <span>tot {time(stop)}{start && stop.toISOString().slice(0, 10) !== start.toISOString().slice(0, 10) ? ' (+1 dag)' : ''}</span>}</span>
            <span className="dayplan-card-body"><span className="dayplan-card-title">{item.actie_omschrijving || 'Afspraak zonder omschrijving'}</span><span className="dayplan-customer">{item.relatie_naam || item.bedrijf || 'Geen klant gekoppeld'}</span>
              <span className="dayplan-people">{codes.length ? codes.map(code => {
                const base = code.replace(/\?$/, '');
                const person = (item.medewerkers || []).find(m => m.code === base);
                return <span key={code} className={`dayplan-person ${isCurrentMedewerker([code], activeCode) ? 'is-current' : ''}`} title={`${person?.volledige_naam || base}${code.endsWith('?') ? ' — onzeker' : ''}`}>{base === 'ALLE' ? 'Iedereen' : code}{code.endsWith('?') && <span className="sr-only"> (onzeker)</span>}</span>;
              }) : <span className="dayplan-unassigned">Nog geen medewerkers</span>}</span>
            </span>
            <span className="dayplan-card-end">{item.locatie && <span className="dayplan-location">{item.locatie}</span>}<span className="dayplan-open" aria-hidden="true">↗</span></span>
          </button>;
        })}</div>
      </section>)}</div>}
  </section>;
}

function AgendaItemDetailModal({ item, onClose, activeMedewerkerCode = '', onKlantInfoClick }) {
  // Get dates directly - they now contain full date+time
  const startDate = getItemStartDate(item);
  const stopDate = getItemStopDate(item);
  const datumDate = parseUTCDate(item.datum ?? item.Datum);
  const normalizedActiveCode = activeMedewerkerCode?.trim().toUpperCase() ?? '';
  const medewerkerCodes = useMemo(
    () => extractMedewerkerCodes(item),
    [item]
  );
  
  const contacts = item.contacts || [];
  const contactsPerPage = 3;
  const totalPages = Math.ceil(contacts.length / contactsPerPage);
  const [currentContactPage, setCurrentContactPage] = useState(1);
  
  const currentContacts = useMemo(() => {
    if (contacts.length === 0) return [];
    const startIndex = (currentContactPage - 1) * contactsPerPage;
    return contacts.slice(startIndex, startIndex + contactsPerPage);
  }, [contacts, currentContactPage]);
  
  useEffect(() => {
    // Reset to page 1 when item changes
    setCurrentContactPage(1);
  }, [item.id, item.datum, item.actie_tijd_start, item.medewerker_veld]);

  const formatDate = (date) => {
    if (!date) return '';
    // Use UTC methods to get date parts (from UTC string)
    const weekdays = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
    const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
    
    // Use UTC methods to get the correct date (from UTC string)
    const day = date.getUTCDate();
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const weekday = weekdays[date.getUTCDay()];
    
    return `${weekday.toUpperCase()} ${day} ${month.toUpperCase()} ${year}`;
  };

  const formatTime = (date) => {
    if (!date) return '';
    // Use UTC methods to get time parts (from UTC string)
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Check if item is type OL (location code is 'OL')
  const isOLType = useMemo(() => {
    const locatie = (item.locatie || item.Locatie || '').toString().trim().toUpperCase();
    return locatie === 'OL';
  }, [item.locatie, item.Locatie]);

  // Parse op locatie times directly (now contain full date)
  const opLocatieStartDate = item.op_locatie_start_tijd || item.OpLocatieStartTijd 
    ? parseUTCDate(item.op_locatie_start_tijd || item.OpLocatieStartTijd)
    : null;
  const opLocatieEindDate = item.op_locatie_eind_tijd || item.OpLocatieEindTijd
    ? parseUTCDate(item.op_locatie_eind_tijd || item.OpLocatieEindTijd)
    : null;

  // Handle map button click
  const handleMapClick = () => {
    const straat = item.straat || item.Straat || '';
    const postCode = item.post_code || item.PostCode || '';
    const plaats = item.plaats || item.Plaats || '';
    
    // Build address string
    const addressParts = [straat, postCode, plaats].filter(part => part && part.trim());
    const address = addressParts.join(', ');
    
    if (address) {
      // Open in Google Maps
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  // Check if address fields are available for map button
  const hasAddress = useMemo(() => {
    const straat = item.straat || item.Straat || '';
    const postCode = item.post_code || item.PostCode || '';
    const plaats = item.plaats || item.Plaats || '';
    return !!(straat || postCode || plaats);
  }, [item.straat, item.Straat, item.post_code, item.PostCode, item.plaats, item.Plaats]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="agenda-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Agenda item details"
      onClick={handleOverlayClick}
    >
      <div className="agenda-detail-modal" role="document">
        <header className="agenda-detail-header">
          <div className="agenda-detail-header-text">
            <span className="agenda-detail-date">{formatDate(datumDate ?? startDate) || '-'}</span>
            <h2 className="agenda-detail-title">{item.relatie_naam || item.RelatieNaam || item.bedrijf || '-'}</h2>
          </div>
          <div className="agenda-detail-time">
            {formatTime(startDate) || '-'}
            {stopDate ? ` - ${formatTime(stopDate)}` : ''}
          </div>
        </header>
        <div className="agenda-detail-content">
          <dl className="agenda-detail-list">
            <div className="agenda-detail-row">
              <dt>Omschrijving</dt>
              <dd>{item.actie_omschrijving || item.ActieOmschrijving || '-'}</dd>
            </div>
            {medewerkerCodes.length > 0 && (
              <div className="agenda-detail-row">
                <dt>Medewerker{medewerkerCodes.length > 1 ? 's' : ''}</dt>
                <dd>{Array.isArray(item.medewerkers) ? item.medewerkers.map(m => (
                  <div key={m.code} className={m.onzeker ? 'medewerker-onzeker' : ''}>
                    {m.code === 'ALLE' ? 'Iedereen (ALLE)' : `${m.volledige_naam || m.code}${m.volledige_naam ? ` (${m.code})` : ''}`}
                    {m.onzeker ? ' — onzeker' : ''}
                  </div>
                )) : medewerkerCodes.join(', ')}</dd>
              </div>
            )}
            {(() => {
              const opmerking = (item.opmerking || item.Opmerking || '').toString().trim();
              return opmerking.length > 2 ? (
                <div className="agenda-detail-row">
                  <dt>Opmerking</dt>
                  <dd>{opmerking}</dd>
                </div>
              ) : null;
            })()}
            {!isOLType && (
              <div className="agenda-detail-row agenda-detail-two-columns">
                <div className="agenda-detail-column">
                  <dt>Locatiecode</dt>
                  <dd>
                    {(item.locatie || item.Locatie || '-') && (
                      <span
                        className={`compact-location-code${
                          (item.locatie ?? item.Locatie ?? '').toString().trim().toUpperCase() === 'VER'
                            ? ' is-ver'
                            : ''
                        }`}
                      >
                        {(item.locatie || item.Locatie || '-').toString().trim() || '-'}
                      </span>
                    )}
                  </dd>
                </div>
                <div className="agenda-detail-column">
                  <dt>Belafspraak</dt>
                  <dd>{(item.belafspraak || item.Belafspraak || 'N').toString().trim() === 'Y' ? 'Ja' : 'Nee'}</dd>
                </div>
              </div>
            )}
            {isOLType && (opLocatieStartDate || opLocatieEindDate) && (
              <div className="agenda-detail-row agenda-detail-two-columns">
                <div className="agenda-detail-column">
                  <dt>Op locatie start tijd</dt>
                  <dd>{opLocatieStartDate ? formatTime(opLocatieStartDate) : '-'}</dd>
                </div>
                <div className="agenda-detail-column">
                  <dt>Op locatie eind tijd</dt>
                  <dd>{opLocatieEindDate ? formatTime(opLocatieEindDate) : '-'}</dd>
                </div>
              </div>
            )}
            {contacts.length > 0 && (
              <div className="agenda-detail-row agenda-detail-contacts">
                <dt>Contactpersonen</dt>
                <dd>
                  <div className="contacts-list">
                    {currentContacts.map((contact, index) => (
                      <div key={index} className="contact-item">
                        <div className="contact-name">{contact.volledige_naam || '-'}</div>
                        {contact.email && (
                          <div className="contact-info">
                            <span className="contact-label">Email:</span>
                            <a href={`mailto:${contact.email}`} className="contact-link">
                              {contact.email}
                            </a>
                          </div>
                        )}
                        {contact.telefoon && (
                          <div className="contact-info">
                            <span className="contact-label">Telefoon:</span>
                            <a href={`tel:${contact.telefoon}`} className="contact-link">
                              {contact.telefoon}
                            </a>
                          </div>
                        )}
                        {contact.telmobiel && (
                          <div className="contact-info">
                            <span className="contact-label">Mobiel:</span>
                            <a href={`tel:${contact.telmobiel}`} className="contact-link">
                              {contact.telmobiel}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="contacts-pagination">
                      <button
                        type="button"
                        className="contacts-pagination-button"
                        onClick={() => setCurrentContactPage(prev => Math.max(1, prev - 1))}
                        disabled={currentContactPage === 1}
                        aria-label="Vorige pagina"
                      >
                        Vorige
                      </button>
                      <span className="contacts-pagination-info">
                        Pagina {currentContactPage} van {totalPages}
                      </span>
                      <button
                        type="button"
                        className="contacts-pagination-button"
                        onClick={() => setCurrentContactPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentContactPage === totalPages}
                        aria-label="Volgende pagina"
                      >
                        Volgende
                      </button>
                    </div>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>
        <footer className="agenda-detail-footer">
          {item.bedrijf && typeof onKlantInfoClick === 'function' && (
            <button 
              type="button" 
              className="agenda-detail-klant-info" 
              onClick={() => onKlantInfoClick(item.bedrijf)}
            >
              Klant info
            </button>
          )}
          {hasAddress && (
            <button 
              type="button" 
              className="agenda-detail-klant-info" 
              onClick={handleMapClick}
            >
              Toon map
            </button>
          )}
          <button type="button" className="agenda-detail-close" onClick={onClose}>
            OK
          </button>
        </footer>
      </div>
    </div>
  );
}

export default App;
